import {
  NextResponse,
  type NextFetchEvent,
  type NextRequest,
} from "next/server";
import { authMiddleware } from "next-firebase-auth-edge";

import { middleware as inlangMiddleware } from "@/lib/i18n";

import { CustomMiddleware } from "./chain";
import { firebaseConfig, serverConfig } from "../firebase/config";

export const PUBLIC_PATHS = [
  "/",
  "/signup",
  "/signin",
  "/reset-password",
  "/pricing",
  "/inneos",
  "/kevin",
  "/lifetime-offer",
  "/fr/signup",
  "/fr/signin",
  "/fr/reset-password",
  "/fr/pricing",
  "/fr/inneos",
  "/fr/kevin",
  "/fr/lifetime-offer",
  "/market",
  "/trump",
  "/fr/kevin",
  "/kevin",
  "/fr/kevin",
  "/trial",
  "/fr/trial",
  "/coin-converter",
  "/fr/coin-converter",
  "/crypto-profit-calculator",
  "/fr/crypto-profit-calculator",
  "/fr/blackfriday",
  "/blackfriday",
  "/terms",
  "/fr/terms",
  "/privacy",
  "/fr/privacy",
  "/smart-allocation/templates/:id",
  "/fr/smart-allocation/templates/:id",
];

function isPublicPath(path: string): boolean {
  // Direct match for static paths
  if (PUBLIC_PATHS.some((publicPath) => publicPath === path)) {
    return true;
  }

  // Check for dynamic path patterns (those containing ':' parameter)
  return PUBLIC_PATHS.some((publicPath) => {
    if (!publicPath.includes(":")) return false;

    // Convert the pattern to a regex
    const regexPattern = publicPath
      .split("/")
      .map((segment) => {
        if (segment.startsWith(":")) {
          return "[^/]+"; // Match any character except '/'
        }
        return segment;
      })
      .join("/");

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  });
}

function redirectToLogin(request: NextRequest) {
  if (
    isPublicPath(request.nextUrl.pathname) ||
    ["/fr"].includes(request.nextUrl.pathname)
  ) {
    return inlangMiddleware(request);
  }
  const url = request.nextUrl.clone();

  const lang = inlangMiddleware(request).headers.get(
    "x-middleware-request-x-language-tag",
  );

  const isCheckout = request.nextUrl.pathname.includes("/checkout");
  const authPage = isCheckout ? "/signup" : "/signin";

  if (lang === "fr") {
    url.pathname = `/` + lang + authPage;
  } else {
    url.pathname = authPage;
  }

  url.searchParams.set("redirect", request.nextUrl.pathname.replace("/fr", ""));

  return NextResponse.redirect(url);
}

function redirectToDashboard(request: NextRequest) {
  if (!isPublicPath(request.nextUrl.pathname)) {
    return inlangMiddleware(request);
  }
  const url = request.nextUrl.clone();

  const lang = inlangMiddleware(request).headers.get(
    "x-middleware-request-x-language-tag",
  );

  if (lang === "fr") {
    url.pathname = `/` + lang + "/dashboard";
  } else {
    url.pathname = `/dashboard`;
  }

  return NextResponse.redirect(url);
}

function withAuthMiddleware(middleware: CustomMiddleware) {
  return async (request: NextRequest, event: NextFetchEvent) => {
    const response = await authMiddleware(request, {
      loginPath: "/api/login",
      logoutPath: "/api/logout",
      apiKey: firebaseConfig.apiKey,
      cookieName: serverConfig.cookieName,
      cookieSignatureKeys: serverConfig.cookieSignatureKeys,
      cookieSerializeOptions: serverConfig.cookieSerializeOptions,
      serviceAccount: serverConfig.serviceAccount,
      handleInvalidToken: async (reason) => {
        console.info("Missing or malformed credentials", { reason });

        return redirectToLogin(request);
      },
      handleError: async (error) => {
        console.error("Unhandled authentication error", { error });

        return redirectToLogin(request);
      },
      handleValidToken: async () => {
        // Authenticated user should not be able to access /signin, /register, and /reset-password routes
        if (
          isPublicPath(request.nextUrl.pathname) &&
          request.nextUrl.pathname !== "/" &&
          request.nextUrl.pathname !== "/pricing" &&
          request.nextUrl.pathname !== "/fr/lifetime-offer" &&
          request.nextUrl.pathname !== "/lifetime-offer" &&
          request.nextUrl.pathname !== "/fr/pricing" &&
          request.nextUrl.pathname !== "/inneos" &&
          request.nextUrl.pathname !== "/fr/inneos" &&
          request.nextUrl.pathname !== "/kevin" &&
          request.nextUrl.pathname !== "/fr/kevin" &&
          request.nextUrl.pathname !== "/market" &&
          request.nextUrl.pathname !== "/trump" &&
          request.nextUrl.pathname !== "/fr/trump" &&
          request.nextUrl.pathname !== "/kevin" &&
          request.nextUrl.pathname !== "/fr/kevin" &&
          request.nextUrl.pathname !== "/trial" &&
          request.nextUrl.pathname !== "/fr/trial" &&
          request.nextUrl.pathname !== "/fr/blackfriday" &&
          request.nextUrl.pathname !== "/blackfriday" &&
          request.nextUrl.pathname !== "/fr/terms" &&
          request.nextUrl.pathname !== "/terms" &&
          request.nextUrl.pathname !== "/fr/privacy" &&
          request.nextUrl.pathname !== "/privacy" &&
          !request.nextUrl.pathname.startsWith(
            "/smart-allocation/templates/",
          ) &&
          !request.nextUrl.pathname.startsWith(
            "/fr/smart-allocation/templates/",
          )
        ) {
          return redirectToDashboard(request);
        }

        return inlangMiddleware(request);
      },
    });

    return middleware(request, event, response);
  };
}

export default withAuthMiddleware;
